import React, { useState, useRef } from 'react';
import { saveAs } from 'file-saver';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

interface JsonToXmlProps {}

const JsonToXml: React.FC<JsonToXmlProps> = () => {
  // State for JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for XML output preview
  const [xmlPreview, setXmlPreview] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for successful conversion
  const [isConverted, setIsConverted] = useState<boolean>(false);
  // State for file name
  const [fileName, setFileName] = useState<string>('converted_data');
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for root element name
  const [rootElement, setRootElement] = useState<string>('root');
  // State for item element name
  const [itemElement, setItemElement] = useState<string>('item');
  // State for indentation
  const [indentSize, setIndentSize] = useState<number>(2);
  // State for XML declaration
  const [includeDeclaration, setIncludeDeclaration] = useState<boolean>(true);
  // State for notification
  const [notification, setNotification] = useState<{type: 'success' | 'error' | 'info' | null, message: string}>({
    type: null,
    message: ''
  });
  
  // Reference to the file input
  const fileInputRef = useRef<HTMLInputElement>(null);
 
  
  // Function to load sample data
  const loadSampleData = () => {
    const formattedSample = JSON.stringify(sampleData, null, 2);
    setJsonInput(formattedSample);
    setError('');
    showNotification('info', 'Sample data loaded!');
  };
  
  // Function to show notification
  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({
      type,
      message
    });
    
    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setNotification({
        type: null,
        message: ''
      });
    }, 3000);
  };
  
  // Function to escape XML special characters
  const escapeXml = (unsafe: string): string => {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
  };
  
  // Function to convert a JSON value to XML
  const jsonValueToXmlString = (
    value: any, 
    nodeName: string, 
    indent: number = 0
  ): string => {
    const spaces = ' '.repeat(indent);
    
    // Handle null or undefined
    if (value === null || value === undefined) {
      return `${spaces}<${nodeName} xsi:nil="true" />`;
    }
    
    // Handle primitives
    if (typeof value !== 'object') {
      const valueStr = typeof value === 'string' 
        ? escapeXml(value) 
        : String(value);
      return `${spaces}<${nodeName}>${valueStr}</${nodeName}>`;
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return `${spaces}<${nodeName} />`;
      }
      
      return value.map(item => {
        if (typeof item === 'object' && item !== null) {
          // For objects in arrays, use the itemElement name
          return jsonValueToXmlString(item, itemElement, indent);
        } else {
          // For primitives in arrays, use a default item name
          return `${spaces}<item>${typeof item === 'string' ? escapeXml(item) : String(item)}</item>`;
        }
      }).join('\n');
    }
    
    // Handle objects
    const childIndent = indent + indentSize;
    const childSpaces = ' '.repeat(childIndent);
    
    // Get all the keys of the object
    const keys = Object.keys(value);
    
    if (keys.length === 0) {
      return `${spaces}<${nodeName} />`;
    }
    
    const childrenXml = keys.map(key => {
      // Sanitize the key for XML element name
      const safeKey = key.replace(/[^a-zA-Z0-9_-]/g, '_');
      return jsonValueToXmlString(value[key], safeKey, childIndent);
    }).join('\n');
    
    return `${spaces}<${nodeName}>\n${childrenXml}\n${spaces}</${nodeName}>`;
  };
  
  // Function to convert JSON to XML
  const convertToXml = (): string => {
    try {
      // Parse the JSON
      const jsonData = JSON.parse(jsonInput);
      
      // Generate XML declaration if needed
      const declaration = includeDeclaration 
        ? '<?xml version="1.0" encoding="UTF-8" ?>\n' 
        : '';
      
      // Handle single objects and arrays differently
      let xmlContent = '';
      
      if (Array.isArray(jsonData)) {
        // For arrays, wrap all items in the root element
        const itemsXml = jsonData.map(item => 
          jsonValueToXmlString(item, itemElement, indentSize)
        ).join('\n');
        
        xmlContent = `${declaration}<${rootElement}>\n${itemsXml}\n</${rootElement}>`;
      } else {
        // For single objects, use the root element directly
        xmlContent = `${declaration}${jsonValueToXmlString(jsonData, rootElement)}`;
      }
      
      return xmlContent;
    } catch (err) {
      throw new Error(`Failed to convert to XML: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };
  
  // Function to validate JSON input and generate preview
  const validateAndPreview = () => {
    setError('');
    setXmlPreview('');
    setIsConverted(false);
    setIsLoading(true);
    
    try {
      // Handle empty input
      if (!jsonInput.trim()) {
        setError('Please enter JSON data or upload a file.');
        setIsLoading(false);
        return;
      }
      
      // Try to parse the JSON to validate it
      JSON.parse(jsonInput);
      
      // Generate XML preview
      const xml = convertToXml();
      setXmlPreview(xml);
      setIsConverted(true);
      setIsLoading(false);
    } catch (err) {
      setError(`${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };
  
  // Function to download the XML
  const downloadXml = () => {
    if (!isConverted) {
      validateAndPreview();
      return;
    }
    
    try {
      // Generate XML if not already generated
      const xml = xmlPreview || convertToXml();
      
      // Create blob and save
      const blob = new Blob([xml], { type: 'application/xml;charset=utf-8;' });
      saveAs(blob, `${fileName}.xml`);
      
      showNotification('success', `XML file downloaded as "${fileName}.xml"`);
    } catch (err) {
      setError(`Failed to download XML: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };
  
  // Function to handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsLoading(true);
    setError('');
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        setJsonInput(text);
        setIsLoading(false);
        showNotification('success', `File "${file.name}" uploaded successfully!`);
      } catch (err) {
        setIsLoading(false);
        setError(`Failed to read the file: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    
    reader.onerror = () => {
      setIsLoading(false);
      setError('Failed to read the file. Please try again.');
    };
    
    reader.readAsText(file);
  };
  
  // Function to clear all inputs
  const clearAll = () => {
    setJsonInput('');
    setXmlPreview('');
    setError('');
    setIsConverted(false);
    setFileName('converted_data');
    setRootElement('root');
    setItemElement('item');
    setIndentSize(2);
    setIncludeDeclaration(true);
    showNotification('info', 'All fields cleared');
  };
  
  // Function to handle drag and drop
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    
    // Check if it's a JSON file
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setError('Please upload a JSON file.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        setJsonInput(text);
        setIsLoading(false);
        showNotification('success', `File "${file.name}" uploaded successfully!`);
      } catch (err) {
        setIsLoading(false);
        setError(`Failed to read the file: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    
    reader.onerror = () => {
      setIsLoading(false);
      setError('Failed to read the file. Please try again.');
    };
    
    reader.readAsText(file);
  };
  
  // Prevent default behavior for drag events
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };
  
  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to XML Converter</h1>
      
      {/* Notification */}
      {notification.type && (
        <div 
          className={`mb-6 p-3 rounded-md ${
            notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
            notification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
            'bg-blue-50 border border-blue-200 text-blue-700'
          }`}
        >
          {notification.message}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">JSON Input</h2>
            <div className="flex space-x-2">
              <button
                className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                onClick={loadSampleData}
              >
                Load Sample
              </button>
              <button
                className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                onClick={() => setJsonInput('')}
              >
                Clear
              </button>
            </div>
          </div>
          
          {/* File Upload Area */}
          <div className="mb-4">
            <div
              className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-blue-400 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={handleFileUpload}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 mx-auto text-gray-400 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-sm text-gray-500">
                Drag & drop a JSON file here, or <span className="text-blue-500">browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">Supports .json files</p>
            </div>
          </div>
          
          {/* JSON Input */}
          <div className="mb-4">
            <textarea
              className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Paste your JSON data here...\n\nExample:\n[\n  {\n    "id": 1,\n    "name": "John Doe",\n    "email": "john@example.com"\n  }\n]`}
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              spellCheck="false"
            />
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={validateAndPreview}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Preview XML'
              )}
            </button>
            
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              onClick={clearAll}
            >
              Clear All
            </button>
          </div>
        </div>
        
        {/* Options & Preview Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">XML Options</h2>
            <button
              className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          {/* Basic Options */}
          <div className="space-y-4 mb-4">
            <div>
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="e.g., my_data_export"
              />
            </div>
            
            <div>
              <label htmlFor="rootElement" className="block text-sm font-medium text-gray-700 mb-1">
                Root Element Name
              </label>
              <input
                type="text"
                id="rootElement"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={rootElement}
                onChange={(e) => setRootElement(e.target.value)}
                placeholder="e.g., root"
              />
            </div>
          </div>
          
          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-4 p-4 mb-4 bg-gray-50 rounded-md border border-gray-200">
              <div>
                <label htmlFor="itemElement" className="block text-sm font-medium text-gray-700 mb-1">
                  Item Element Name (for arrays)
                </label>
                <input
                  type="text"
                  id="itemElement"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={itemElement}
                  onChange={(e) => setItemElement(e.target.value)}
                  placeholder="e.g., item"
                />
              </div>
              
              <div>
                <label htmlFor="indentSize" className="block text-sm font-medium text-gray-700 mb-1">
                  Indentation Size
                </label>
                <input
                  type="number"
                  id="indentSize"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={indentSize}
                  min={0}
                  max={8}
                  onChange={(e) => setIndentSize(parseInt(e.target.value) || 0)}
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeDeclaration"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={includeDeclaration}
                  onChange={(e) => setIncludeDeclaration(e.target.checked)}
                />
                <label htmlFor="includeDeclaration" className="ml-2 block text-sm text-gray-700">
                  Include XML Declaration
                </label>
              </div>
            </div>
          )}
          
          {/* XML Preview */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">XML Preview</h3>
            {xmlPreview ? (
              <div className="relative">
                <pre className="w-full h-56 p-3 border border-gray-300 rounded-md font-mono text-xs overflow-auto bg-gray-50">
                  {xmlPreview}
                </pre>
                <button
                  className="absolute top-2 right-2 bg-white p-1 rounded-md border border-gray-300 hover:bg-gray-100"
                  onClick={() => {
                    navigator.clipboard.writeText(xmlPreview);
                    showNotification('info', 'XML copied to clipboard');
                  }}
                  title="Copy to clipboard"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="w-full h-56 p-3 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                <p>XML preview will appear here</p>
              </div>
            )}
          </div>
          
          {/* Download Button */}
          <button
            className={`w-full px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isConverted
                ? 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={downloadXml}
            disabled={!isConverted}
          >
            <span className="flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download XML
            </span>
          </button>
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold mb-4">About JSON to XML Conversion</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How It Works</h3>
            <p className="text-gray-600 text-sm">
              This tool converts JSON data into XML format. It supports both simple and complex data structures, 
              including nested objects and arrays. The converter automatically maps JSON properties to XML 
              elements and handles special characters properly.
            </p>
            
            <h3 className="font-medium text-gray-800 mt-4 mb-2">Supported Features</h3>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li>Convert JSON objects and arrays to XML</li>
              <li>Customizable root element name</li>
              <li>Customizable item element name for arrays</li>
              <li>Proper handling of nested structures</li>
              <li>XML special character escaping</li>
              <li>Configurable indentation</li>
              <li>Optional XML declaration</li>
              <li>Live preview before download</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
              <li>Ensure your JSON is valid before conversion</li>
              <li>Choose meaningful names for root and item elements</li>
              <li>XML element names cannot contain spaces or special characters</li>
              <li>Consider using a consistent naming convention for better readability</li>
              <li>For large datasets, preview a sample first to verify structure</li>
            </ul>
            
            <h3 className="font-medium text-gray-800 mt-4 mb-2">XML Format Details</h3>
            <p className="text-gray-600 text-sm">
              XML (eXtensible Markup Language) is a markup language that defines rules for encoding documents in a format 
              that is both human-readable and machine-readable. XML is widely used for data exchange between systems,
              configuration files, and storage of structured data.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToXml;